import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Sheet, PrimaryButton, fieldStyle } from './Sheet.js';
import { UploadSheet } from './UploadSheet.js';
import { formatExact } from './MoneyText.js';

/**
 * The one way to add anything to Wealth (Navigation Law 1: one primary action
 * per screen).
 *
 * Three entry paths exist — a statement, a deposit, a policy — and three buttons
 * would be three primary actions competing for the same thumb. So the screen
 * keeps one verb, "Add", and the choice happens after the tap, where there is
 * room to explain what each one means.
 */

type Choice = 'statement' | 'deposit' | 'policy';

export function AddSheet({ onClose }: { onClose: () => void }) {
  const [choice, setChoice] = useState<Choice | null>(null);

  if (choice === 'statement') return <UploadSheet onClose={onClose} />;
  if (choice === 'deposit') return <DepositSheet onClose={onClose} />;
  if (choice === 'policy') return <PolicySheet onClose={onClose} />;

  return (
    <Sheet title="Add to your wealth" onClose={onClose}>
      <Option
        title="Upload a statement"
        detail="CDSL eCAS or CAMS/KFintech CAS. Holdings, units and values are read for you."
        onClick={() => setChoice('statement')}
      />
      <Option
        title="Add a fixed deposit"
        detail="No statement arrives for an FD, so the terms are typed once and the interest is computed from then on."
        onClick={() => setChoice('deposit')}
      />
      <Option
        title="Add an insurance policy"
        detail="Life or medical. Cover is recorded and tracked, but protection is not counted as wealth."
        onClick={() => setChoice('policy')}
      />
    </Sheet>
  );
}

function Option({ title, detail, onClick }: { title: string; detail: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'var(--color-fill-subtle)', border: 'none',
        borderRadius: 'var(--radius-control)', padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)', color: 'inherit', minHeight: 44,
      }}
    >
      <span style={{ display: 'block', font: 'var(--type-headline)' }}>{title}</span>
      <span style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)' }}>{detail}</span>
    </button>
  );
}

interface MemberOption {
  id: string;
  name: string;
}

function useMembers() {
  return useQuery({ queryKey: ['members'], queryFn: () => api.get<MemberOption[]>('/members') });
}

/** Rupees typed by a human → integer paise. Money never travels as a float. */
function toPaise(rupees: string): number {
  const cleaned = rupees.replace(/[^0-9.]/g, '');
  if (!cleaned) return 0;
  const [whole, frac = ''] = cleaned.split('.');
  return Number(whole || '0') * 100 + Number(`${frac}00`.slice(0, 2));
}

function DepositSheet({ onClose }: { onClose: () => void }) {
  const members = useMembers();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    memberId: '',
    bank: '',
    principal: '',
    rate: '',
    startDate: '',
    maturityDate: '',
    payout: 'cumulative',
    compounding: 'quarterly',
  });
  const [failure, setFailure] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = useMutation({
    mutationFn: () =>
      api.post<{ maturityValue: { amount: number } }>('/manual/deposits', {
        memberId: form.memberId,
        bank: form.bank.trim(),
        principalPaise: toPaise(form.principal),
        // Basis points: 7.15% becomes 715, so the rate stays an integer all the
        // way down to the arithmetic.
        rateBps: Math.round(Number(form.rate) * 100),
        startDate: form.startDate,
        maturityDate: form.maturityDate,
        payout: form.payout,
        compounding: form.compounding,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (err) => setFailure((err as Error).message),
  });

  const ready =
    form.memberId && form.bank.trim() && toPaise(form.principal) > 0 && Number(form.rate) > 0 &&
    form.startDate && form.maturityDate;

  return (
    <Sheet title="Add a fixed deposit" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFailure(null);
          create.mutate();
        }}
      >
        <select style={fieldStyle} value={form.memberId} onChange={set('memberId')} required>
          <option value="">Whose deposit?</option>
          {(members.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <input style={fieldStyle} placeholder="Bank" value={form.bank} onChange={set('bank')} required />
        <input
          style={fieldStyle}
          placeholder="Principal (₹)"
          inputMode="decimal"
          value={form.principal}
          onChange={set('principal')}
          required
        />
        <input
          style={fieldStyle}
          placeholder="Interest rate % (e.g. 7.15)"
          inputMode="decimal"
          value={form.rate}
          onChange={set('rate')}
          required
        />
        <label style={{ font: 'var(--type-caption)', color: 'var(--color-label-secondary)' }}>Start date</label>
        <input style={fieldStyle} type="date" value={form.startDate} onChange={set('startDate')} required />
        <label style={{ font: 'var(--type-caption)', color: 'var(--color-label-secondary)' }}>Maturity date</label>
        <input style={fieldStyle} type="date" value={form.maturityDate} onChange={set('maturityDate')} required />

        <select style={fieldStyle} value={form.payout} onChange={set('payout')}>
          <option value="cumulative">Interest compounds (cumulative)</option>
          <option value="periodic">Interest paid out to me (periodic)</option>
        </select>
        <select style={fieldStyle} value={form.compounding} onChange={set('compounding')}>
          <option value="quarterly">Compounded quarterly (usual)</option>
          <option value="monthly">Compounded monthly</option>
          <option value="half_yearly">Compounded half-yearly</option>
          <option value="annually">Compounded annually</option>
        </select>

        <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)' }}>
          The maturity amount is computed and shown once saved — check it against the bank’s receipt.
          A mismatch usually means a mistyped rate or date.
        </p>

        {failure && <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-critical)' }}>{failure}</p>}
        <PrimaryButton type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? 'Saving…' : 'Add deposit'}
        </PrimaryButton>
      </form>
    </Sheet>
  );
}

function PolicySheet({ onClose }: { onClose: () => void }) {
  const members = useMembers();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    ownerMemberId: '',
    kind: 'life',
    subtype: 'term',
    insurer: '',
    sumAssured: '',
    premium: '',
    premiumFrequency: 'annually',
    nextDueDate: '',
    surrenderValue: '',
  });
  const [failure, setFailure] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Only endowment and ULIP have a surrender value. Term and medical cover pay
  // out on an event that has not happened, so there is nothing to surrender —
  // the field is hidden rather than shown-and-rejected.
  const hasSurrender = form.kind === 'life' && (form.subtype === 'endowment' || form.subtype === 'ulip');

  const create = useMutation({
    mutationFn: () =>
      api.post('/manual/policies', {
        ownerMemberId: form.ownerMemberId,
        kind: form.kind,
        ...(form.kind === 'life' ? { subtype: form.subtype } : {}),
        insurer: form.insurer.trim(),
        sumAssuredPaise: toPaise(form.sumAssured),
        premiumPaise: toPaise(form.premium),
        premiumFrequency: form.premiumFrequency,
        ...(form.nextDueDate ? { nextDueDate: form.nextDueDate } : {}),
        ...(hasSurrender && toPaise(form.surrenderValue) > 0
          ? { surrenderValuePaise: toPaise(form.surrenderValue) }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (err) => setFailure((err as Error).message),
  });

  const ready = form.ownerMemberId && form.insurer.trim() && toPaise(form.sumAssured) > 0;

  return (
    <Sheet title="Add an insurance policy" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFailure(null);
          create.mutate();
        }}
      >
        <select style={fieldStyle} value={form.ownerMemberId} onChange={set('ownerMemberId')} required>
          <option value="">Whose policy?</option>
          {(members.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <select style={fieldStyle} value={form.kind} onChange={set('kind')}>
          <option value="life">Life</option>
          <option value="medical">Medical</option>
        </select>
        {form.kind === 'life' && (
          <select style={fieldStyle} value={form.subtype} onChange={set('subtype')}>
            <option value="term">Term</option>
            <option value="endowment">Endowment</option>
            <option value="ulip">ULIP</option>
          </select>
        )}
        <input style={fieldStyle} placeholder="Insurer" value={form.insurer} onChange={set('insurer')} required />
        <input
          style={fieldStyle}
          placeholder={form.kind === 'medical' ? 'Sum insured (₹)' : 'Sum assured (₹)'}
          inputMode="decimal"
          value={form.sumAssured}
          onChange={set('sumAssured')}
          required
        />
        <input style={fieldStyle} placeholder="Premium (₹)" inputMode="decimal" value={form.premium} onChange={set('premium')} />
        <select style={fieldStyle} value={form.premiumFrequency} onChange={set('premiumFrequency')}>
          <option value="annually">Yearly premium</option>
          <option value="half_yearly">Half-yearly</option>
          <option value="quarterly">Quarterly</option>
          <option value="monthly">Monthly</option>
          <option value="single">Single premium</option>
        </select>
        <label style={{ font: 'var(--type-caption)', color: 'var(--color-label-secondary)' }}>Next premium due</label>
        <input style={fieldStyle} type="date" value={form.nextDueDate} onChange={set('nextDueDate')} />

        {hasSurrender ? (
          <>
            <input
              style={fieldStyle}
              placeholder="Current surrender value (₹, optional)"
              inputMode="decimal"
              value={form.surrenderValue}
              onChange={set('surrenderValue')}
            />
            <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)' }}>
              Only this figure counts toward net worth, and it is always shown as an estimate — it is
              your reading of what the insurer would pay today, not a market price.
            </p>
          </>
        ) : (
          <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)' }}>
            This cover is protection, not an asset, so it is tracked but never added to your net
            worth. The payout depends on an event that has not happened.
          </p>
        )}

        {failure && <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-critical)' }}>{failure}</p>}
        <PrimaryButton type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? 'Saving…' : 'Add policy'}
        </PrimaryButton>
      </form>
    </Sheet>
  );
}

/** Exported for the Wealth screen's deposit rows. */
export { formatExact };
